import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

export const loginSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** Password is only required when re-enrolling over an already-active secret. */
export const totpEnrollSchema = z.object({
  password: z.string().min(1).optional(),
});
export type TotpEnrollInput = z.infer<typeof totpEnrollSchema>;

export const totpVerifyEnrollmentSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});
export type TotpVerifyEnrollmentInput = z.infer<typeof totpVerifyEnrollmentSchema>;

export const totpDisableSchema = z.object({
  password: z.string().min(1),
});
export type TotpDisableInput = z.infer<typeof totpDisableSchema>;

/** A login-time code is either a 6-digit TOTP or a recovery code — the service tells them apart. */
export const totpLoginVerifySchema = z.object({
  code: z.string().min(1),
});
export type TotpLoginVerifyInput = z.infer<typeof totpLoginVerifySchema>;

/** Office passwords: long rather than complex. Checked when a password is set, never at login (old ones stay valid). */
export const PASSWORD_MIN_LENGTH = 12;

/** `POST /users/:id/reset-password` — an admin sets a temporary password; the target must change it on next login. */
export const resetPasswordSchema = z.object({
  password: z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(256),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/** `POST /auth/password/complete` — the forced-change step clearing a reset-issued password. Kept separate from `resetPasswordSchema` even though the shape matches today: an admin setting someone else's password and a user setting their own answer different questions and can evolve independently. */
export const completePasswordChangeSchema = z.object({
  password: z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(256),
});
export type CompletePasswordChangeInput = z.infer<typeof completePasswordChangeSchema>;

export const provisionUserSchema = z.object({
  email: z.email().trim(),
  name: z.string().trim().min(1).max(120),
  password: z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(256),
  /** Role keys to assign, replacing whatever the user currently holds. Omitted leaves roles untouched. */
  roles: z.array(z.string()).optional(),
});
export type ProvisionUserInput = z.infer<typeof provisionUserSchema>;

/** `POST /users` — the admin "Create user" screen. Unlike `provisionUserSchema`, a duplicate email is always rejected, never a silent reactivate. */
export const createUserSchema = z.object({
  email: z.email().trim(),
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(1).max(32).optional(),
  password: z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(256),
  roles: z.array(z.string()).optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/** `PATCH /users/:id/roles` — sets the user's roles to exactly this list; an empty array removes all roles. */
export const updateUserRolesSchema = z.object({
  roles: z.array(z.string()),
});
export type UpdateUserRolesInput = z.infer<typeof updateUserRolesSchema>;

export const USER_STATUSES = ['active', 'inactive'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** `GET /users` — search matches name, email or phone; `role` keeps users holding any of the given roles. */
export const userListQuerySchema = defineListQuery({
  sortFields: ['name', 'email', 'lastLoginAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(USER_STATUSES)),
    role: listFilter(z.string().min(1).max(64)),
  },
});
export type UserListQuery = z.infer<typeof userListQuerySchema>;

/** `POST /auth/impersonation` — an admin starts acting as another user, for testing. `reason` lands on the audit entry. */
export const startImpersonationSchema = z.object({
  userId: z.uuid(),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type StartImpersonationInput = z.infer<typeof startImpersonationSchema>;
